/**
 * Hero banner for Student Loan Contribution pages.
 *
 * Renders the route title on the shared `PageHero` recipe with a responsive
 * grid of metric tiles. The tile set varies by plan-limit shape (annual /
 * lifetime / both / none):
 *
 *   variant 1 — annual limit  : annual-usage, lifetime, last?, next
 *   variant 2 — lifetime only : lifetime-usage, last?, next
 *   variant 3 — no limit      : year, lifetime, last?, next
 *   variant 4 — both limits   : annual-usage, lifetime-usage, last?, next
 *
 * The 4 layouts share 6 tile kinds. We list which kinds belong to each
 * variant, then let the card grid (container-query auto-fit) drive how many
 * columns to render. No per-variant grid markup, no duplicated card JSX.
 */
import Component from '@glimmer/component';
import { concat } from '@ember/helper';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import style from 'ember-style-modifier/modifiers/style';
import { or } from 'tio-ui/utilities';
import type { TOC } from '@ember/component/template-only';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Chip from 'tio-ui/components/chip';
import PageHero from 'tio-ui/components/page-hero';
import TooltipButton from 'tio-ui/components/tooltip-button';
import { formatCurrency } from 'tio-ui/format';
import type IntlService from 'ember-intl/services/intl';
import {
  PhCalendarBlank,
  PhCheckCircle,
  PhPaperPlaneTilt,
  PhGift,
  PhMoneyWavy,
  PhProhibit,
  PhWarningCircle,
} from 'ember-phosphor-icons';

export interface ContributionHeroSignature {
  Args: {
    greeting: string;
    firstName: string;
    variant: 1 | 2 | 3 | 4;
    /** Route title for the heading. Defaults to the contributions title. */
    title?: string;
    /**
     * Document level of the heading. A landing page passes 1 so the hero is
     * the page's only h1; a page that already renders its own title leaves
     * the default so the outline does not gain a second one.
     */
    headingLevel?: 1 | 2 | 3;
    /**
     * Renders the metric tiles. Pass false for page states with no
     * contribution figures to report — the hero still carries the title.
     */
    showMetrics?: boolean;
    /** Renders the LRAP "Enrolled" status chip alongside the hero actions. */
    lrapEnrolled?: boolean;
    blocked?: boolean;
    annualUsage?: number;
    annualLimit?: number;
    annualProgressPercent?: number;
    lifetimeUsage?: number;
    lifetimeLimit?: number;
    lifetimeProgressPercent?: number;
    lifetimeContribution?: number;
    lifetimeAsOfDate?: string;
    currentYear?: number;
    yearContribution?: number;
    paymentCount?: number;
    lastPaymentAmount?: number;
    lastPaymentDate?: string;
    nextContribution: number;
    nextContributionDate: string;
    /** Loan-standing verification is blocking the next contribution (strikethrough + red note). */
    nextContributionRequiresVerification?: boolean;
    nextContributionVerificationLabel?: string;
    onViewBenefit?: () => void;
  };
}

// Local TOC tiles — each renders a nested Card with a label/value top and a
// status footer at the bottom, pushed down by the column's space-between so
// footers line up across a row. Tiles with progress bars put the bar between
// the value and the footer.
interface UsageCardArgs {
  label: string;
  usage: number;
  limit: number;
  progressPercent: number;
  limitReached: boolean;
  limitLabel: string;
}

const UsageCard: TOC<{ Args: UsageCardArgs }> = <template>
  <Card
    @radius="nested"
    @padding="sm"
    class="tio-flex-between flex-col gap-3"
    role="group"
    aria-label={{@label}}
  >
    <div class="tio-col-extra-tight">
      <div class="tio-text-overline tio-fg-muted">{{@label}}</div>
      <div class="tio-text-2xl-bold tio-fg-primary">{{formatCurrency @usage}}</div>
    </div>
    <div class="tio-col-tight">
      <div
        class="w-full h-2 tio-bg-track rounded-full overflow-hidden flex"
        role="progressbar"
        aria-valuenow={{@progressPercent}}
        aria-valuemin={{0}}
        aria-valuemax={{100}}
        aria-label={{@label}}
      >
        <div
          class="h-full app-progress-fill--brand"
          {{style width=(concat @progressPercent "%")}}
        ></div>
      </div>
      <div class="tio-row-between tio-text-xs-medium tio-fg-muted">
        {{#if @limitReached}}
          <span>{{t "slr.reimagined.limit_reached"}}</span>
          <PhProhibit class="tio-icon-xs" />
        {{else}}
          <span>
            {{@limitLabel}}
            {{formatCurrency @limit}}
          </span>
          <PhCalendarBlank class="tio-icon-xs" />
        {{/if}}
      </div>
    </div>
  </Card>
</template>;

interface FooterCardArgs {
  label: string;
  value: number;
  footerLabel: string;
  icon: typeof PhCheckCircle;
}

const FooterCard: TOC<{ Args: FooterCardArgs }> = <template>
  <Card
    @radius="nested"
    @padding="sm"
    class="tio-flex-between flex-col gap-3"
    role="group"
    aria-label={{@label}}
  >
    <div class="tio-col-extra-tight">
      <div class="tio-text-overline tio-fg-muted">{{@label}}</div>
      <div class="tio-text-2xl-bold tio-fg-primary">{{formatCurrency @value}}</div>
    </div>
    <div class="tio-row-between tio-text-xs-medium tio-fg-muted">
      <span>{{@footerLabel}}</span>
      {{#let @icon as |Icon|}}
        <Icon class="tio-icon-xs" />
      {{/let}}
    </div>
  </Card>
</template>;

interface NextContributionCardArgs {
  amount: number;
  date: string;
  blocked: boolean;
  requiresVerification?: boolean;
  /**
   * Label under the struck stat — the newest page states vary it by blocker:
   * "requires verification" (overdue), "Not Eligible" (loan status),
   * "No Eligible Accounts" (parent PLUS). Falls back to the verification copy.
   */
  verificationLabel?: string;
}

const NextContributionCard: TOC<{ Args: NextContributionCardArgs }> = <template>
  <Card
    @radius="nested"
    @padding="sm"
    class="tio-flex-between flex-col gap-3"
    role="group"
    aria-label={{t "slr.reimagined.next_contribution"}}
  >
    <div class="tio-col-extra-tight">
      <div class="tio-text-overline tio-fg-muted">
        {{t "slr.reimagined.next_contribution"}}
      </div>
      <div
        class="tio-text-2xl-bold
          {{if
            @requiresVerification
            'tio-fg-danger line-through'
            (if @blocked 'tio-fg-muted line-through' 'tio-fg-primary')
          }}"
        data-test-next-contribution-amount
      >
        {{formatCurrency @amount}}
      </div>
    </div>
    <div
      class="tio-row-between tio-text-xs-medium
        {{if (or @blocked @requiresVerification) 'tio-fg-danger' 'tio-fg-muted'}}"
    >
      {{#if @requiresVerification}}
        <span data-test-next-contribution-requires-verification>
          {{if
            @verificationLabel
            @verificationLabel
            (t "slr.loan_standing.attention.requires_verification")
          }}
        </span>
        {{#if @verificationLabel}}
          {{! Hard-ineligible labels ("Not Eligible", "No Eligible Accounts")
              are self-explanatory and the attention section is suppressed for
              these states — the "verify under Needs Your Attention" tooltip
              would point at a section that doesn't exist. Decorative icon only. }}
          <PhWarningCircle
            class="tio-icon-xs"
            aria-hidden="true"
            data-test-next-contribution-blocked-icon
          />
        {{else}}
          <TooltipButton
            @tooltip={{t "slr.reimagined.requires_verification_tooltip"}}
            @icon={{PhWarningCircle}}
            @iconSize={{16}}
            @appearance="minimal"
            @size="sm"
            aria-label={{t "slr.reimagined.requires_verification_tooltip"}}
            data-test-next-contribution-requires-verification-info
          />
        {{/if}}
      {{else if @blocked}}
        <span>{{t "slr.reimagined.unable_to_process"}}</span>
        <TooltipButton
          @tooltip={{t "slr.reimagined.unable_to_process_tooltip"}}
          @icon={{PhWarningCircle}}
          @iconSize={{16}}
          @appearance="minimal"
          @size="sm"
          aria-label={{t "slr.reimagined.unable_to_process_tooltip"}}
          data-test-next-contribution-blocked-info
        />
      {{else}}
        <span>{{t "slr.reimagined.expected_date" date=@date}}</span>
        <PhPaperPlaneTilt class="tio-icon-xs" />
      {{/if}}
    </div>
  </Card>
</template>;

export default class ContributionHero extends Component<ContributionHeroSignature> {
  @service declare intl: IntlService;

  handleViewBenefit = () => {
    this.args.onViewBenefit?.();
  };

  get title() {
    return this.args.title ?? this.intl.t('slr.reimagined.your_student_loan_contributions');
  }

  // PageHero's own default is 1, so the level has to be resolved here for a
  // page that already titles itself to keep a single h1.
  get headingLevel() {
    return this.args.headingLevel ?? 2;
  }

  get greetingLine() {
    return `${this.args.greeting}, ${this.args.firstName}`;
  }

  get showMetrics() {
    return this.args.showMetrics ?? true;
  }

  get isAnnualLimitReached() {
    return (this.args.annualProgressPercent ?? 0) >= 100;
  }

  get isLifetimeLimitReached() {
    return (this.args.lifetimeProgressPercent ?? 0) >= 100;
  }

  /**
   * Per-tile getters — each variant decides which subset to render, then
   * template order decides the layout. Replaces the 4 hand-rolled variant
   * blocks (each ~80 lines of duplicated inline card markup) with one
   * declarative path per tile kind.
   */
  get showAnnualUsage() {
    return this.args.variant === 1 || this.args.variant === 4;
  }

  get showLifetimeUsage() {
    return this.args.variant === 2 || this.args.variant === 4;
  }

  get showLifetimeContribution() {
    return this.args.variant === 1 || this.args.variant === 3;
  }

  get showYearContribution() {
    return this.args.variant === 3;
  }

  get showLastPayment() {
    return !!this.args.lastPaymentDate;
  }

  <template>
    <PageHero
      @icon={{PhMoneyWavy}}
      @title={{this.title}}
      @subtitle={{this.greetingLine}}
      @headingLevel={{this.headingLevel}}
      @gradient="brand-soft"
      class="tio-elevation-card-raised"
      data-test-slr-contribution-hero
    >
      <:actions>
        {{#if @lrapEnrolled}}
          <Chip @intent="success" @size="sm" data-test-lrap-enrolled-chip>
            {{t "lrap.enrollment.tile.enrolled"}}
          </Chip>
        {{/if}}
        {{#if @onViewBenefit}}
          <Button
            @intent="primary"
            @appearance="outlined"
            {{on "click" this.handleViewBenefit}}
            data-test-slr-benefit-details-button
          >
            {{t "slr.reimagined.benefit_details_link"}}
          </Button>
        {{/if}}
      </:actions>
      <:default>
        {{! Metric grid — one card grid drives column count via container query.
            Variant decides which tiles to render; the grid decides layout. }}
        {{#if this.showMetrics}}
          <div class="app-card-grid app-card-grid--medium mt-6">
            {{#if this.showAnnualUsage}}
              <UsageCard
                @label={{t "slr.reimagined.annual_usage"}}
                @usage={{or @annualUsage 0}}
                @limit={{or @annualLimit 0}}
                @progressPercent={{or @annualProgressPercent 0}}
                @limitReached={{this.isAnnualLimitReached}}
                @limitLabel={{t "slr.reimagined.annual_limit"}}
              />
            {{/if}}
            {{#if this.showLifetimeUsage}}
              <UsageCard
                @label={{t "slr.reimagined.lifetime_usage"}}
                @usage={{or @lifetimeUsage 0}}
                @limit={{or @lifetimeLimit 0}}
                @progressPercent={{or @lifetimeProgressPercent 0}}
                @limitReached={{this.isLifetimeLimitReached}}
                @limitLabel={{t "slr.reimagined.lifetime_limit"}}
              />
            {{/if}}
            {{#if this.showLifetimeContribution}}
              <FooterCard
                @label={{t "slr.reimagined.lifetime_contribution"}}
                @value={{or @lifetimeContribution 0}}
                @footerLabel={{if
                  @lifetimeAsOfDate
                  (t "slr.reimagined.as_of_date" date=@lifetimeAsOfDate)
                  (t "slr.reimagined.over_lifetime")
                }}
                @icon={{PhGift}}
              />
            {{/if}}
            {{#if this.showYearContribution}}
              <FooterCard
                @label={{t "slr.reimagined.year_contribution" year=@currentYear}}
                @value={{or @yearContribution 0}}
                @footerLabel={{concat
                  (or @paymentCount 0)
                  " "
                  (t "slr.reimagined.contribution_count" count=(or @paymentCount 0))
                }}
                @icon={{PhMoneyWavy}}
              />
            {{/if}}
            {{#if this.showLastPayment}}
              <FooterCard
                @label={{t "slr.reimagined.last_contribution"}}
                @value={{or @lastPaymentAmount 0}}
                @footerLabel={{t "slr.reimagined.sent_date" date=@lastPaymentDate}}
                @icon={{PhCheckCircle}}
              />
            {{/if}}
            <NextContributionCard
              @amount={{@nextContribution}}
              @date={{@nextContributionDate}}
              @blocked={{if @blocked true false}}
              @requiresVerification={{@nextContributionRequiresVerification}}
              @verificationLabel={{@nextContributionVerificationLabel}}
            />
          </div>
        {{/if}}
      </:default>
    </PageHero>
  </template>
}
